import { Colors, CYCLE_PAGE_LAYOUT, PAGE_LAYOUT } from '@amelio/ui-library';

import { isProgressComplete } from '@/utils/objectiveProgress';

/**
 * Class strings shared by every Objectives page. They are constants rather than utilities in
 * `app.css` on purpose: keeping them as Tailwind classes leaves the emitted CSS untouched, so
 * moving a block from one page to another cannot shift a single pixel.
 */

/** The page shell: centered and capped at the same width as the rest of the Performance module. */
export const OBJECTIVES_PAGE_LAYOUT = {
    /**
     * The full shell every Objectives page renders its `Container` with: capped and centered
     * (neither the library's `Container` nor the host centers it on its own), with the same
     * padding rhythm on every page. Non-overlapping breakpoint pairs throughout (`max-md:`/`md:`,
     * `max-md:`/`md:` again for the top from `PAGE_LAYOUT.topPadding`), not a base utility next to
     * its own variant: a base utility beats its own `md:`/`lg:`/`xl:` variant in this Tailwind v4
     * build (same flat-cascade reason as `.obj-header`). The top padding is the library's shared
     * `PAGE_LAYOUT.topPadding` token (96px from md up, 48px below -- the host adds zero vertical
     * padding to the Performance module, same value `PageContainer` uses); the bottom stays a flat
     * 48px, the same value the shell's old `xl:py-12` topped out at. The sides are the
     * reference's app-shell gutter (16px, then 24px from md up), which this host does not provide:
     * without it the period help touches the viewport edge and the pillar carousel's `-m-3` bleed
     * scrolls the page sideways.
     */
    shell: `${CYCLE_PAGE_LAYOUT.maxWidth} mx-auto max-md:px-4 md:px-6 ${PAGE_LAYOUT.topPadding} pb-[48px]`,
} as const;

/** The prototype's translucent surface: a white hairline over a 45% white fill, lightly blurred. */
export const GLASS_SURFACE = 'rounded-[16px] border border-white bg-white/45 backdrop-blur-[2px]';

/** The near-opaque panel a glass frame wraps on the objective form. */
export const WHITE_PANEL_SURFACE = 'rounded-[16px] border border-white bg-white/90';

/**
 * The white section the objectives and pillar pages group their owner cards in: a wider,
 * more opaque cousin of the glass surface, sitting straight on the page background. Its
 * `mt-6 md:mt-9` / `p-5 md:p-[42px_38px]` pair is backed by `.obj-owner-panel` in `app.css` --
 * plain CSS, not a `md:` pair, for the same flat-cascade reason as `.obj-header`.
 */
export const OWNER_LIST_PANEL =
    'obj-owner-panel mb-6 rounded-[16px] border border-white bg-white/60';

/** The module's block title: 24px navy medium. */
export const SECTION_TITLE = 'text-navy text-[24px] font-medium';

/**
 * The same title on the detail page, where the reference steps it down on mobile
 * (`text-lg md:text-2xl`) as it does for the hero title and the Discussion heading beside it.
 * A constant of its own rather than a variant of `SECTION_TITLE`, because `SECTION_TITLE_TIGHT`
 * derives from that one and the form and pillar page hold their titles at a fixed 24px. Backed by
 * `.obj-detail-section-title` in `app.css` -- plain CSS, not a `md:` pair, for the same
 * flat-cascade reason as `.obj-header`.
 */
export const DETAIL_SECTION_TITLE = 'obj-detail-section-title text-navy font-medium';

/**
 * The same title with the prototype's tighter line box. Most blocks use it; the detail page's
 * description, measurement and pillar headings deliberately keep the default line height, as
 * the reference does.
 */
export const SECTION_TITLE_TIGHT = `${SECTION_TITLE} leading-[28px]`;

/**
 * The strategic-alignment card's own title: one size step below `SECTION_TITLE_TIGHT` so
 * "Alignement stratégique" stays on one line next to the badge at the card's 675px width and the
 * page's 1352px cap -- the prototype's own text (a different font stack) clears that width at
 * 24px, Poppins medium does not.
 */
export const ALIGNMENT_CARD_TITLE = 'text-navy text-[22px] font-medium leading-[28px] flex-1';

/**
 * The responsible person's name atop an owner group (pillar page and overview alike): the same
 * 24px step and line box as `SECTION_TITLE_TIGHT`, since it heads a section of its own.
 */
export const GROUP_OWNER_NAME = `${SECTION_TITLE} leading-[28px]`;

/**
 * A type-choice card on the "what kind of objective" page: no `backdrop-blur`, unlike
 * `GLASS_SURFACE` -- the prototype keeps these cards crisp. Full width on mobile, the
 * prototype's fixed 434px from `md:` up -- backed by `.obj-type-card-width` in `app.css` for the
 * same flat-cascade reason as `.obj-header`.
 */
export const TYPE_CHOICE_CARD =
    'obj-type-card-width h-[369px] rounded-[16px] bg-white/45 border border-white hover:bg-white/60 transition-all';

/**
 * Geometry of the measurement rail, shared by the "Objectif principal" head card and the
 * cards hung below it. Everything hangs off one abscissa: the centre of the head card's target
 * icon -- 1px border + 20px padding + half of the 48px icon = 45px from the section's left
 * edge. A 3px dashed line centred there starts at 43.5px; rounded to whole pixels that is
 * `ml-[43px]` for the rail and `left-[42px]` for the lead-in, which is measured from the card's
 * padding edge, 1px further right. Both lines therefore sit at 43-46px; the half pixel off the
 * exact centre is invisible. Change one of these numbers and you must change them all.
 */
export const MEASUREMENT_SPINE_HEAD =
    'relative flex items-center gap-3 rounded-[16px] border border-white p-5 backdrop-blur-[2px]';
export const MEASUREMENT_SPINE_HEAD_ICON = 'h-[48px] w-[48px] bg-white';

/**
 * The dashed purple rail the measurement cards hang off, on the form and the detail page alike.
 * The rail itself is no longer drawn here: each `MeasurementSpineItem` now carries its own
 * `border-l`, stopping cleanly at the last card instead of running past it into an opaque mask
 * (see `MeasurementSpine.tsx`). Its whole geometry -- the indent, the line's width,
 * the gap to the card and the dot's offset -- lives in the `.obj-spine*` rules of `app.css`,
 * derived there from the single abscissa documented above; they also fold the rail away below
 * 768px, where the reference drops it entirely and the cards stand on their own.
 */
export const MEASUREMENT_SPINE = 'obj-spine relative flex flex-col';

/**
 * The connector from the "Objectif principal" head card's target icon down to the first
 * measurement card's dot -- a short stub *inside* that
 * card, separate from `MEASUREMENT_SPINE` below it. Backed by `.obj-spine-lead-in` in `app.css`
 * -- plain CSS, not a `md:` pair, for the same flat-cascade reason as `.obj-header`: a bare
 * `hidden md:block` here never shows at any width.
 */
export const MEASUREMENT_SPINE_LEAD_IN =
    'obj-spine-lead-in absolute left-[42px] top-[68px] -bottom-[56px] w-0 border-l-[3px] border-dashed border-obj-accent';

/**
 * The one surface every filter control of a toolbar wears -- search, attribute filter, cycle --
 * taken from the library's glass multi-select (`MultiSelectGlass`), whose trigger cannot be
 * restyled: translucent white, white hairline, blurred. The `!` beats the opaque white and grey
 * border the search bar and select paint by default, which they concatenate without merging.
 */
export const TOOLBAR_CONTROL_SURFACE = '!border !border-white !bg-white/70 backdrop-blur-sm';

/**
 * The turquoise-bordered pill around a key result's value and its `ValueStepper`. The pill is the field's frame, so it draws the focus ring -- only while the
 * value has focus, leaving a focused stepper button with its own single ring.
 */
export const VALUE_PILL =
    'border-primary bg-surface-field flex items-center rounded-full border has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-primary-800';

/**
 * The bare number inside `VALUE_PILL`: no frame of its own, not even the library's focus border,
 * and no native spin buttons doubling the stepper beside it. Centring has to reach the `input`,
 * which does not inherit `text-align` from the frame `TextInput` puts its classes on.
 */
export const VALUE_PILL_INPUT =
    'text-navy h-auto rounded-none border-transparent bg-transparent px-0 font-medium focus-within:border-transparent [&_input]:text-center';

/** The module's empty-state paragraph. */
export const EMPTY_STATE_TEXT = 'text-obj-slate text-[14px] italic';

/** The employee objectives list when the window holds none, in the profile's placeholder tone. */
export const EMPLOYEE_OBJECTIVES_EMPTY_TEXT = 'text-growth-slate text-[14px] italic';

/**
 * Rounded pill showing a percentage: the library's turquoise button gradient once complete
 * (`.obj-badge-complete`, matching `KEY_RESULT_FILL`'s bar gradient instead of the
 * prototype's flat `bg-primary`), purple while in progress.
 */
export const progressBadgeClassName = (percentage: number): string =>
    `rounded-full px-3 py-1 font-medium text-white ${
        isProgressComplete(percentage) ? 'obj-badge-complete' : 'bg-obj-accent'
    }`;

/**
 * A key result's fill: a turquoise gradient, not the plain purple `bg-obj-accent` used for the
 * spine and badges -- the bar is the card's only turquoise touch, and it reads as violet-on-violet
 * without it.
 */
export const KEY_RESULT_FILL = 'bg-gradient-to-r from-obj-turquoise-deep to-obj-turquoise';

/**
 * The detail page's hero title: the prototype steps it down on mobile
 * (`text-lg md:text-2xl leading-6 md:leading-7`), unlike `SECTION_TITLE_TIGHT`'s fixed 24px used
 * by every other block title. Backed by `.obj-detail-hero-title` in `app.css` -- plain CSS, not a
 * `md:` pair, for the same flat-cascade reason as `.obj-header`.
 */
export const DETAIL_HERO_TITLE = 'obj-detail-hero-title text-navy font-medium';

/**
 * The overview page's own hero title: the prototype steps it down on mobile
 * (`text-xl md:text-2xl`), unlike `SECTION_TITLE_TIGHT`'s fixed 24px used by every other block
 * title. Backed by `.obj-header-hero-title` in `app.css` -- plain CSS, not a `md:` pair, for the
 * same flat-cascade reason as `DETAIL_HERO_TITLE`.
 */
export const OVERVIEW_HERO_TITLE = 'obj-header-hero-title text-navy font-medium';

/** A small translucent pill laid over a pillar's own gradient, like the objective count on its tile. */
export const ON_COLOR_PILL =
    'rounded-[38px] bg-white/45 px-3 py-1 text-[11px] font-medium text-white';

/**
 * The boxes a pillar tile is built from, read both by the card and by the placeholder that holds
 * the row while its totals are being read: they are the same shape by construction, so what
 * arrives lands exactly where the placeholder stood. Its own width and height are `app.css`'s
 * concern (`.obj-pillar-tile`, `.obj-pillar-card`).
 */
export const PILLAR_TILE = {
    surface: 'obj-pillar-card relative flex h-full flex-col overflow-hidden rounded-2xl p-6',
    header: 'relative mb-[18px] flex flex-shrink-0 items-start justify-between',
    iconBox: 'h-[65px] w-[65px] flex-shrink-0 rounded-[10px]',
    /** The bottom margin clears the progress bar, which is laid over the tile rather than in it. */
    statsBlock: 'relative mb-[44px] flex flex-shrink-0 flex-col gap-[18px]',
    progressAnchor: 'absolute right-6 bottom-6 left-6',
    progressTrack: 'h-[11px] w-full overflow-hidden rounded-full',
} as const;

/**
 * The tone the module's other placeholders draw on a white surface (meetings, statistics), so an
 * Objectives block loads like the rest of the application rather than as grey tiles with white bars.
 */
export const PLACEHOLDER_BONE_COLOR = Colors.BASE_100;
